import { IStairConfig, IVec3 } from '@stair-craft/common/util';

import { RISER_BOARD_THICKNESS } from '../../constants';

/** How far a kosour's notch faces step back for the riser boards: their thickness, or nothing on an open stair. */
export function riserSeatOf(config: IStairConfig): number {
    return config.risersEnabled ? RISER_BOARD_THICKNESS : 0;
}

/** The horizontal unit vector from one profile point to another, or null when they stand one above the other. */
function travel(from: IVec3, to: IVec3): { x: number; z: number } | null {
    const length: number = Math.hypot(to.x - from.x, to.z - from.z);
    return length > 1e-6 ? { x: (to.x - from.x) / length, z: (to.z - from.z) / length } : null;
}

/** The flight direction at a vertical rise (from the run after it, else the run before), or null when the step is no rise. */
function riseDirection(profile: readonly IVec3[], index: number): { x: number; z: number } | null {
    const previous: IVec3 = profile[index - 1];
    const point: IVec3 = profile[index];
    if (travel(previous, point) !== null) {
        return null;
    }
    const next: IVec3 | undefined = profile.at(index + 1);
    const forward: { x: number; z: number } | null = next ? travel(point, next) : null;
    if (forward !== null) {
        return forward;
    }
    return index >= 2 ? travel(profile[index - 2], previous) : null;
}

/**
 * Moves every rise of a sawtooth back along the flight by `seat`: a riser board then covers the
 * board's end from outside instead of standing inside it (SC-CALC-089). The tread before the rise
 * keeps its seat, and the run under it grows by `seat`.
 */
function seatRisers(profile: readonly IVec3[], seat: number): IVec3[] {
    const seated: IVec3[] = profile.slice(0, 1);
    for (let index: number = 1; index < profile.length; index++) {
        const previous: IVec3 = profile[index - 1];
        const point: IVec3 = profile[index];
        const ahead: { x: number; z: number } | null = point.y > previous.y ? riseDirection(profile, index) : null;
        if (ahead === null) {
            seated.push(point);
            continue;
        }
        // The run's old end lies on the lengthened run: only the profile's first point stays.
        if (seated.length > 1) {
            seated.pop();
        }
        seated.push({ x: previous.x + ahead.x * seat, y: previous.y, z: previous.z + ahead.z * seat });
        seated.push({ x: point.x + ahead.x * seat, y: point.y, z: point.z + ahead.z * seat });
    }
    return seated;
}

/**
 * Turns a sawtooth that traces the tread tops into the kosour's cut line: every tread sits on
 * the board, so the cut runs a tread thickness lower (a point on the floor stays on the floor).
 * A flight that ends at the slab drops the last rise — the slab edge, not the board, carries it.
 * With risers on, `riserSeat` is their board thickness: every rise steps back by it.
 */
export function kosourTopLine(treadProfile: readonly IVec3[], treadThickness: number, endsAtSlab: boolean, riserSeat: number = 0): IVec3[] {
    // The last rise is not seated: the rise to the slab is dropped below, and the rise into a turn
    // stands against the turn block, so its riser stops at the board's inner face (`markTurnRisers`).
    const seated: IVec3[] = riserSeat > 0 ? seatRisers(treadProfile.slice(0, -1), riserSeat) : treadProfile.slice(0, -1);
    const seatedProfile: IVec3[] = [...seated, ...treadProfile.slice(-1)];
    const lowered: IVec3[] = seatedProfile.map((point: IVec3) => ({ ...point, y: point.y > 0 ? point.y - treadThickness : point.y }));
    const trimmed: IVec3[] = endsAtSlab ? lowered.slice(0, -1) : lowered;
    return trimmed.filter((point: IVec3, index: number) => {
        if (index === 0) {
            return true;
        }
        const previous: IVec3 = trimmed[index - 1];
        return Math.hypot(point.x - previous.x, point.y - previous.y, point.z - previous.z) > 1e-6;
    });
}
